import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, FolderOpen, Star, Download, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, download } from "@/lib/api";
import { Loader, StatusBadge, EmptyState } from "@/components/brand";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUSES = [["all", "All"], ["draft", "Draft"], ["under_review", "Under review"], ["approved", "Approved"], ["issued", "Issued"], ["superseded", "Superseded"], ["archived", "Archived"]];

export default function Library() {
  const [docs, setDocs] = useState(null);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [cat, setCat] = useState("all");
  const [fav, setFav] = useState(false);
  const [sel, setSel] = useState([]);
  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data));
  }, []);
  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/documents", { params: { q: q || undefined, status: status === "all" ? undefined : status, category: cat === "all" ? undefined : cat, favourites: fav || undefined } }).then((r) => setDocs(r.data));
    }, 250);
    return () => clearTimeout(t);
  }, [q, status, cat, fav]);
  const today = new Date().toISOString().slice(0, 10);
  const zip = () => download(`/documents/export-zip?ids=${sel.join(",")}`, "saflr-documents.zip").catch(() => toast.error("ZIP export failed"));
  return (
    <div className="mx-auto max-w-7xl space-y-6" data-testid="library-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div><p className="eyebrow">Controlled documents</p><h1 className="mt-2 text-3xl font-bold">Document library</h1></div>
        <div className="flex gap-2">
          {sel.length > 0 && <Button variant="outline" onClick={zip} data-testid="bulk-zip"><Download className="mr-1.5 h-4 w-4" />Download {sel.length} as ZIP</Button>}
          <Button asChild className="rounded-full"><Link to="/app/generate"><Sparkles className="mr-1.5 h-4 w-4" />New document</Link></Button>
        </div>
      </div>
      <div className="flex flex-wrap gap-3">
        <div className="relative min-w-[240px] flex-1"><Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, number, project, tag…" className="pl-9" data-testid="library-search" /></div>
        <Select value={cat} onValueChange={setCat}><SelectTrigger className="w-52" data-testid="library-category-filter"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All categories</SelectItem>{cats.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent></Select>
        <Button variant={fav ? "default" : "outline"} onClick={() => setFav(!fav)} data-testid="library-favourites"><Star className="mr-1.5 h-4 w-4" />Favourites</Button>
      </div>
      <div className="flex flex-wrap gap-1.5">{STATUSES.map(([v, l]) => <button key={v} onClick={() => setStatus(v)} data-testid={`status-filter-${v}`} className={`rounded-full px-3.5 py-1.5 text-sm transition-colors ${status === v ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"}`}>{l}</button>)}</div>
      {!docs ? <Loader /> : docs.length === 0 ? <EmptyState icon={FolderOpen} title="No documents found" text="Create a document with AI or adjust your filters." action={<Button asChild className="rounded-full"><Link to="/app/generate">Create with AI</Link></Button>} /> : (
        <div className="overflow-x-auto rounded-2xl border bg-card">
          <table className="w-full text-sm">
            <thead className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground"><tr><th className="w-10 p-4" /><th className="p-4">Document</th><th className="p-4">Category</th><th className="p-4">Status</th><th className="p-4">Version</th><th className="p-4">Review date</th><th className="p-4">Updated</th></tr></thead>
            <tbody>{docs.map((d) => {
              const due = ["approved", "issued"].includes(d.status) && d.review_date && d.review_date < today;
              return (
                <tr key={d.id} className="border-b last:border-0 hover:bg-muted/40" data-testid={`doc-row-${d.id}`}>
                  <td className="p-4"><input type="checkbox" checked={sel.includes(d.id)} onChange={(e) => setSel(e.target.checked ? [...sel, d.id] : sel.filter((x) => x !== d.id))} /></td>
                  <td className="p-4"><Link to={`/app/documents/${d.id}`} className="font-medium hover:underline">{d.title}</Link><p className="font-mono text-xs text-muted-foreground">{d.doc_number}{d.project_ref && ` · ${d.project_ref}`}</p></td>
                  <td className="p-4 text-muted-foreground">{d.category_name}</td>
                  <td className="p-4"><div className="flex flex-wrap gap-1"><StatusBadge status={d.status} />{due && <StatusBadge status="review_due" />}</div></td>
                  <td className="p-4 font-mono text-xs">v{d.version}</td>
                  <td className={`p-4 font-mono text-xs ${due ? "text-alert" : ""}`}>{d.review_date || "—"}</td>
                  <td className="p-4 text-xs text-muted-foreground">{d.updated_at.slice(0, 10)}</td>
                </tr>
              );
            })}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
